import React, {
  useEffect,
  useRef,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import classnames from "classnames";
import {
  makeSpotlightPeersSelector,
  videoBoxesSelector,
  screenConsumerSelector,
  screenProducersSelector,
} from "../../helpers/Selectors";
import RoomContext from "../../context/RoomContext";
import Me from "../Presence/Me";
import Peer from "../Presence/Peer";
import SpeakerPeer from "../Presence/SpeakerPeer";
import Grid from "@material-ui/core/Grid";
import debounce from "../../utils/debounce";
import { setDisplayMode } from "../../actions/roomActions";

const RATIO = 1.334;
const PADDING_V = 50;
const PADDING_H = 0;
const FILMSTRING_PADDING_V = 50;
const FILMSTRING_PADDING_H = 0;

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      borderRadius: 24,
      height: "100%",
      width: "100%",
      display: "grid",
      gridTemplateColumns: "1fr",
      gridTemplateRows: "1fr 0.25fr",
      [theme.breakpoints.down("xs")]: {
        borderRadius: 0,
        width: "100%",
      },
      paddingBottom: 80,
      "&.screenShareLayout": {
        gridTemplateRows: "1fr 0.18fr",
      },
    },
    speaker: {
      borderRadius: 24,
      gridArea: "1 / 1 / 1 / 1",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: 0,
      [theme.breakpoints.down("xs")]: {
        borderRadius: 0,
        flexDirection: "column",
      },
    },
    filmStrip: {
      gridArea: "2 / 1 / 2 / 1",
      minHeight: 0,
    },
    filmItem: {
      display: "flex",
      borderRadius: 24,
      [theme.breakpoints.down("xs")]: {
        borderRadius: 0,
      },
      "&.selected": {
        borderColor: "rgba(55, 126, 255, 1)",
      },
      "&.active": {
        opacity: "0.6",
      },
    },
    hiddenToolBar: {
      paddingTop: 0,
      transition: "padding .5s",
    },
    showingToolBar: {
      transition: "padding .5s",
    },
  })
);

const FilmStrip = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { roomClient } = useContext(RoomContext);
  const rootRef = useRef();
  const activePeerRef = useRef();
  const filmStripRef = useRef();

  const [lastSpeaker, setLastSpeaker] = useState();
  const [speakerWidth, setSpeakerWidth] = useState();
  const [speakerHeight, setSpeakerHeight] = useState();
  const [filmStripWidth, setFilmStripWidth] = useState();
  const [filmStripHeight, setFilmStripHeight] = useState();

  const activeSpeakerId = useSelector((state) => state.room.activeSpeakerId);
  const selectedPeerId = useSelector((state) => state.room.selectedPeerId);
  const peers = useSelector((state) => state.peers);
  const room = useSelector((state) => state.room);
  const consumers = useSelector((state) => state.consumers);
  const myId = useSelector((state) => state.me.id);
  const screenConsumers = useSelector(screenConsumerSelector);
  const screenProducers = useSelector(screenProducersSelector);
  const spotlights = useSelector(
    (state) => makeSpotlightPeersSelector(state)(state),
    shallowEqual
  );
  const boxes = useSelector((state) => videoBoxesSelector(state));
  const toolbarsVisible = useSelector((state) => state.room.toolbarsVisible);
  const permanentTopBar = useSelector(
    (state) => state.settings.permanentTopBar
  );
  const hasWebinarPresenterPermission = useSelector((state) =>
    state.me.roles.some((role) =>
      state.room.permissionsFromRoles.MODERATE_WEBINAR.includes(role)
    )
  );

  const iAmSharingScreen = screenProducers.length > 0;
  const remoteScreenPeerId = useMemo(() => {
    const active = (screenConsumers || []).find(
      (c) => c && !c.locallyPaused && !c.remotelyPaused && c.peerId
    );
    return active ? active.peerId : null;
  }, [screenConsumers]);

  const screenSharePeerId = iAmSharingScreen ? myId : remoteScreenPeerId;
  const hasScreenShare = Boolean(screenSharePeerId);

  // Ekran paylaşımı varken filmstrip'e zorla
  useEffect(() => {
    if (hasScreenShare && room.mode !== "filmstrip") {
      dispatch(setDisplayMode("filmstrip"));
    }
  }, [hasScreenShare, room.mode, dispatch]);

  const getActivePeerId = useCallback(() => {
    if (selectedPeerId && peers[selectedPeerId]) {
      return selectedPeerId;
    }

    if (lastSpeaker && peers[lastSpeaker]) {
      return lastSpeaker;
    }

    const peerIds = Object.keys(peers);

    if (peerIds.length > 0) {
      return peerIds[0];
    }
  }, [selectedPeerId, peers, lastSpeaker]);

  // Konuşan ekran paylaşımını ezmesin
  const stagePeerId = hasScreenShare ? screenSharePeerId : getActivePeerId();
  const stageIsMe = hasScreenShare && stagePeerId === myId;
  const activePeerId = getActivePeerId();

  const updateDimensions = useCallback(() => {
    const root = rootRef.current;

    const availableWidth = root ? root.clientWidth : 0;
    const availableSpeakerHeight =
      root.clientHeight * (hasScreenShare ? 0.82 : 0.8) -
      (toolbarsVisible || permanentTopBar ? PADDING_V : PADDING_H);

    const availableFilmstripHeight =
      root.clientHeight * (hasScreenShare ? 0.18 : 0.2);

    const speaker = activePeerRef.current;

    if (speaker) {
      let speakerWidth;
      let speakerHeight;

      if (hasScreenShare) {
        // Paylaşılan ekranı mümkün olduğunca büyük tut
        speakerWidth = availableWidth - 16;
        speakerHeight = availableSpeakerHeight - 16;
      } else {
        speakerWidth = availableWidth - PADDING_H;
        speakerHeight = speakerWidth / RATIO - 60;
        if (speakerHeight > availableSpeakerHeight - PADDING_V) {
          speakerHeight = availableSpeakerHeight - PADDING_V;
          speakerWidth = speakerHeight * RATIO;
        }
      }

      setSpeakerWidth(speakerWidth);
      setSpeakerHeight(speakerHeight);
    }

    const filmStrip = filmStripRef.current;

    if (filmStrip) {
      let filmStripHeight = availableFilmstripHeight - FILMSTRING_PADDING_V;

      let filmStripWidth = filmStripHeight * RATIO;

      if (filmStripWidth * boxes > availableWidth - FILMSTRING_PADDING_H) {
        filmStripWidth = (availableWidth - FILMSTRING_PADDING_H) / boxes;
        filmStripHeight = filmStripWidth / RATIO;
      }
      setFilmStripWidth(filmStripWidth);
      setFilmStripHeight(filmStripHeight);
    }
  }, [
    boxes,
    permanentTopBar,
    toolbarsVisible,
    hasScreenShare,
  ]);

  useEffect(() => {
    const debouncedUpdate = debounce(updateDimensions, 250);
    window.addEventListener("resize", debouncedUpdate);

    updateDimensions();

    return () => {
      window.removeEventListener("resize", debouncedUpdate);
    };
  }, [updateDimensions]);

  useEffect(() => {
    // Paylaşım varken lastSpeaker sahneyi değiştirmesin
    if (hasScreenShare) return;
    if (activeSpeakerId != null && activeSpeakerId !== myId) {
      setLastSpeaker(activeSpeakerId);
    }
  }, [activeSpeakerId, myId, hasScreenShare]);

  return (
    <div
      className={classnames(
        classes.root,
        hasScreenShare && "screenShareLayout",
        toolbarsVisible || permanentTopBar
          ? classes.showingToolBar
          : classes.hiddenToolBar
      )}
      ref={rootRef}
    >
      <div className={classes.speaker} ref={activePeerRef}>
        {stageIsMe && (
          <Me
            style={{
              width: speakerWidth,
              height: speakerHeight,
            }}
            onFilmStrip={false}
            screenOnly
          />
        )}

        {!stageIsMe &&
          peers[stagePeerId] &&
          (!room.webinar || (room.webinar && spotlights.length !== 0)) && (
            <SpeakerPeer
              id={stagePeerId}
              style={{
                width: speakerWidth,
                height: speakerHeight,
              }}
              onFilmStrip={false}
              screenOnly={hasScreenShare}
            />
          )}

        {!hasScreenShare && room.webinar && spotlights.length === 0 && (
          <Me
            style={{
              width: speakerWidth,
              height: speakerHeight,
            }}
            onFilmStrip={false}
          />
        )}
      </div>

      <div className={classes.filmStrip} ref={filmStripRef}>
        <Grid container justify="center" spacing={0}>
          {(!room.webinar ||
            !hasWebinarPresenterPermission ||
            (room.webinar && spotlights.length !== 0)) && (
            <Grid item>
              <div
                className={classnames(classes.filmItem, {
                  active: myId === activePeerId || stageIsMe,
                })}
              >
                <Me
                  style={{
                    width: filmStripWidth,
                    height: filmStripHeight,
                    backgroundSize: "contain",
                  }}
                  onFilmStrip={true}
                />
              </div>
            </Grid>
          )}

          {Object.keys(peers).map((peerId) => {
            if (
              spotlights.find(
                (spotlightsElement) => spotlightsElement === peerId
              )
            ) {
              return (
                <Grid key={peerId} item>
                  <div
                    key={peerId}
                    onClick={() => {
                      // Paylaşım varken tıklama sahneyi kamera konuşanına çevirmesin
                      if (hasScreenShare) return;
                      roomClient.setSelectedPeer(peerId);
                    }}
                    className={classnames(classes.filmItem, {
                      selected: !hasScreenShare && selectedPeerId === peerId,
                      active:
                        peerId === activePeerId ||
                        (hasScreenShare && peerId === stagePeerId),
                    })}
                  >
                    <Peer
                      id={peerId}
                      style={{
                        width: filmStripWidth,
                        height: filmStripHeight,
                        backgroundSize: "contain",
                      }}
                      onFilmStrip={true}
                    />
                  </div>
                </Grid>
              );
            } else {
              return "";
            }
          })}
        </Grid>
      </div>
    </div>
  );
};

export default FilmStrip;
